import { Button, Callout, LayoutHeader, LayoutPage, YStack } from '@shinetools/lumen-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'

import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { APIError } from '../../../../utils'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { HeaderSupplierDetail } from './elements/HeaderSupplierDetail'
import { SupplierBillInformation } from './elements/SupplierBillInformation'
import { SupplierDetailInformation } from './elements/SupplierDetailInformation'

interface SupplierUrlParams {
  supplierId: string
}

type SupplierUrlParamsGeneric = Pick<SupplierUrlParams, keyof SupplierUrlParams>

export const SupplierDetail = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { supplierId } = useParams<SupplierUrlParamsGeneric>()
  const { contact, error, isError, isLoading, refetch } = useFetchContactById(supplierId)
  const { url: suppliersUrl } = useOrganizationViewUrl(reactRoute.organization.suppliers.getMainRoute())

  const handleBackButtonClick = () => {
    if (suppliersUrl) {
      navigate(suppliersUrl)
      return
    }

    navigate(-1)
  }

  const handleRetryButtonClick = () => {
    refetch()
  }

  const fallbackHeader = (
    <LayoutHeader title={t('navigation.menu.label.expenses.suppliers')} onBack={handleBackButtonClick} />
  )

  // No id means the contact query never runs. That is still loading, not "not found":
  // the not-found state is only for a lookup that came back missing or not a supplier.
  if (!supplierId || isLoading) {
    return (
      <LayoutPage header={fallbackHeader} layout="list-table">
        {null}
      </LayoutPage>
    )
  }

  if (isError || !contact?.isSupplier) {
    const isNotFound =
      (error instanceof APIError && error.statusCode === 404) || (!isError && (!contact || !contact.isSupplier))
    const message = isNotFound ? t('ds.nav_list.not_found') : t('suppliers.detail.error.fetch_supplier')

    return (
      <LayoutPage header={fallbackHeader} layout="list-table">
        <YStack gap={16}>
          <Callout color={isNotFound ? 'informative' : 'danger'} message={message} />
          {isError && !isNotFound ? (
            <Button label={t('suppliers.detail.error.retry')} onClick={handleRetryButtonClick} variant="tonal" />
          ) : null}
        </YStack>
      </LayoutPage>
    )
  }

  return (
    <LayoutPage
      header={<HeaderSupplierDetail contact={contact} />}
      layout="details-view"
      left={
        <YStack gap={24}>
          {contact.isArchived ? <Callout color="warning" message={t('suppliers.detail.callout.archived')} /> : null}
          <SupplierBillInformation supplierId={supplierId} />
        </YStack>
      }
      right={<SupplierDetailInformation contact={contact} />}
    />
  )
}
